import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Auth from '../components/Auth';
import whiteoutLogo from '../assets/whiteout-logo.png';
import { DISPLAY_CONFIG } from '../config/display';
import type { AdminError, PlayerQuery } from './adminApi';
import { AdminAccessContext } from './adminAccess';
import LivePanel from './LivePanel';
import OverviewPanel from './OverviewPanel';
import PlayersPanel, { DEFAULT_PLAYER_QUERY } from './PlayersPanel';
import PlayerProfile from './PlayerProfile';
import ReportsPanel from './ReportsPanel';

type Tab = 'overview' | 'players' | 'reports';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'players', label: 'Players' },
  { id: 'reports', label: 'Reports' },
];

const readTab = (): Tab => {
  const requested = new URLSearchParams(window.location.search).get('tab');
  return TABS.find(item => item.id === requested)?.id ?? 'overview';
};

function writeTab(tab: Tab) {
  const params = new URLSearchParams(window.location.search);
  if (tab === 'overview') params.delete('tab'); else params.set('tab', tab);
  const search = params.toString();
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`);
}

const BackToGame: React.FC = () => <a className="admin-back" href="/">Back to game</a>;

const NotAuthorised: React.FC = () => (
  <div className="admin-denied">
    <div className="panel admin-denied-card">
      <img src={whiteoutLogo} alt="" aria-hidden="true" />
      <p className="section-label">Operator dashboard</p>
      <h1>You're not authorised to view this page.</h1>
      <BackToGame />
    </div>
  </div>
);

/**
 * Operator dashboard at /admin (spec 005). The live panel is always on top;
 * Overview, Players and Reports are tabs. Overview and Players are read-only;
 * Reports is where player reports about riders are dismissed or answered with a
 * name reset. Access is decided by the server on every request: a 403 from any
 * panel switches to "not authorised", a 401 ends the session and shows the
 * sign-in form.
 */
const AdminDashboard: React.FC = () => {
  const { user, token, isAuthenticated, isLoading, endSession } = useAuth();
  const [access, setAccess] = useState<'ok' | 'forbidden' | 'signin'>('ok');
  const [tab, setTab] = useState<Tab>(readTab);
  const [playerQuery, setPlayerQuery] = useState<PlayerQuery>(DEFAULT_PLAYER_QUERY);
  const [openPlayer, setOpenPlayer] = useState<string | null>(null);

  // A new session (signing in again, or as someone else) gets a fresh verdict.
  useEffect(() => { setAccess('ok'); }, [token]);

  const report = useCallback((error: AdminError) => {
    if (error.kind === 'forbidden') setAccess('forbidden');
    if (error.kind === 'signin') {
      setAccess('signin');
      endSession();
    }
  }, [endSession]);

  const selectTab = (next: Tab) => {
    setTab(next);
    writeTab(next);
  };

  if (isLoading) return <div className="loading-screen"><p>Checking your session…</p></div>;
  if (!isAuthenticated || !user || access === 'signin') return <Auth />;
  if (user.role !== 'admin' || access === 'forbidden') return <NotAuthorised />;

  return (
    <AdminAccessContext.Provider value={report}>
      <div className="app-shell admin-shell">
        <header className="app-header admin-header">
          <div className="brand-lockup">
            <img className="brand-mark" src={whiteoutLogo} alt="" aria-hidden="true" />
            <div>
              <h1>{DISPLAY_CONFIG.brandName}</h1>
              <p>Operator dashboard</p>
            </div>
          </div>
          <div className="header-status"><span>Operator</span></div>
          <div className="header-actions"><BackToGame /></div>
        </header>

        <main className="admin-main">
          <LivePanel />

          <div className="admin-tabs" role="tablist" aria-label="Dashboard sections">
            {TABS.map(item => (
              <button
                key={item.id}
                id={`admin-tab-${item.id}`}
                role="tab"
                type="button"
                aria-selected={tab === item.id}
                aria-controls={`admin-panel-${item.id}`}
                onClick={() => selectTab(item.id)}
              >{item.label}</button>
            ))}
          </div>

          <section
            id={`admin-panel-${tab}`}
            role="tabpanel"
            aria-labelledby={`admin-tab-${tab}`}
            className="panel admin-tabpanel"
          >
            {tab === 'overview' && <OverviewPanel />}
            {tab === 'players' && (openPlayer
              ? <PlayerProfile id={openPlayer} onBack={() => setOpenPlayer(null)} />
              : <PlayersPanel query={playerQuery} onQueryChange={setPlayerQuery} onOpen={setOpenPlayer} />)}
            {tab === 'reports' && <ReportsPanel />}
          </section>
        </main>
      </div>
    </AdminAccessContext.Provider>
  );
};

export default AdminDashboard;
